import useSWR from 'swr';
import fetchJsonp from 'fetch-jsonp';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';

const TRUSTYOU_API_KEY = 'fbf1c3fb-ad8e-479f-bd0f-f6963be1c519';

type SummaryText = {
  summaryText: string;
  summarySubtext: string;
};

export type Category = {
  id: string;
  name: string;
  score: number;
  count: number;
  text: string;
  summarySentenceList: string[];
} & SummaryText;

export type GoodToKnow = {
  text: string;
  score: number;
  sentiment: string;
};

export type RatingByType = {
  type: string;
  score: string;
  percentage: number;
  summaryText: string;
  categories: Category[];
  goodToKnowList: GoodToKnow[];
};

export type RatingByLanguage = {
  language: string;
  percentage: number;
  score: string;
  summaryText: string;
  categories: Category[];
  goodToKnowList: GoodToKnow[];
  ratingByTypeList: RatingByType[];
};

export type RatingBadge = {
  type: string;
  text: string;
  subtext: string;
};

export type MetaReview = {
  reviewCount: number;
  score: number;
  scoreDescription: string;
  summaryText: string;
  categories: Category[];
  allCategories: Category[];
  goodToKnowList: GoodToKnow[];
  ratingByTypeList: RatingByType[];
  ratingByLanguage: RatingByLanguage[];
  badges: RatingBadge[];
};

const tyFetcher = (url: string) =>
  process.env.NODE_ENV === 'test' // TODO: check if we can mock fetchJsonp in storybook
    ? fetch(url)
        .then((response) => response.json())
        .then((data) => data.response)
    : fetchJsonp(url, {
        // TrustYou API must be called client-side using JSONP (https://github.com/TrustYou/example-hotel-details/blob/gh-pages/js/main.js line 51)
        jsonpCallbackFunction: 'trustyou_meta_review',
        jsonpCallback: '',
        nonce: '',
        referrerPolicy: '',
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.meta.code === 200) {
            return data.response;
          }
          return data;
        });

const options = (trustYouHotelId?: string) => ({
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  shouldRetryOnError: false,
  onError: (error: Error) => {
    clientAppLog({
      level: 'WARN',
      codeSourceName: 'TrustYouMetaReview',
      logMessage: `name:${error?.name} message:${error?.message}`,
      url: `https://api.trustyou.com/hotels/${trustYouHotelId}/meta_review.json`,
    });
  },
});

const maxSummaryTextLength = 65;

const mapCategory = (category: CategoryResponse) => {
  const { category_name, score, text, count, summary_sentence_list, category_id } = category;

  return {
    id: `${category_id}`,
    name: category_name,
    score,
    count,
    text,
    summarySentenceList:
      summary_sentence_list
        ?.map((summarySentence: SummarySentenceListResponse): string => summarySentence.text)
        .slice(0, 1) || [],
    ...summary_sentence_list.reduce(
      (result: SummaryText, actual: SummarySentenceListResponse): SummaryText => {
        const actualText = actual.text.endsWith('.') ? actual.text : `${actual.text}.`;
        let newResult: SummaryText;
        if (result.summaryText.length === 0) {
          newResult = {
            ...result,
            summaryText: actualText,
          };
        } else if (result.summaryText.length + actualText.length <= maxSummaryTextLength) {
          newResult = {
            ...result,
            summaryText: [result.summaryText, actualText].join(' '),
          };
        } else {
          newResult = {
            ...result,
            summarySubtext: [result.summarySubtext, actualText].join(' '),
          };
        }
        return newResult;
      },
      {
        summaryText: '',
        summarySubtext: '',
      }
    ),
  };
};

const mapGoodToKnow = (goodToKnow: GoodToKnowResponse): GoodToKnow => ({
  text: goodToKnow.text,
  score: goodToKnow.score,
  sentiment: goodToKnow.sentiment,
});

const getSummaryText = (goodToKnowList: GoodToKnow[]): string =>
  goodToKnowList
    .filter(({ sentiment }: GoodToKnow): boolean => sentiment === 'pos')
    .map(({ text }: GoodToKnow): string => (text.endsWith('.') ? text : `${text}.`))
    .join(' ');

const mapRatingByType = (tripType: TripTypeMetaReviewListResponse): RatingByType => {
  const goodToKnowList = tripType.good_to_know_list
    ?.filter((goodToKnow: GoodToKnowResponse): boolean => goodToKnow.category_id !== '39') // OPX-2721: Filtering category "Sustainability" (id 39 in TrustYou response)
    ?.map(mapGoodToKnow);
  return {
    type: tripType.filter.trip_type,
    score: tripType.summary.score,
    percentage: tripType.reviews_percent,
    categories: tripType.category_list
      ?.filter((category: CategoryResponse): boolean => category.category_id !== '39') // OPX-2721: Filtering category "Sustainability" (id 39 in TrustYou response)
      ?.map(mapCategory),
    goodToKnowList,
    summaryText: getSummaryText(goodToKnowList),
  };
};

// eslint-disable-next-line
const tyMapper = (tyResponse: any): MetaReview | undefined => {
  if (!tyResponse) return;

  // OPX-2721: Filtering category "Sustainability" (id 39 in TrustYou response)
  const filteredCategories = tyResponse?.category_list?.filter(
    (category: CategoryResponse): boolean => category.category_id !== '39'
  );

  const orderedCategories = filteredCategories?.sort((a: CategoryResponse, b: CategoryResponse) => {
    if (a.score < b.score) {
      return 1;
    }
    if (a.score > b.score) {
      return -1;
    }
    return 0;
  });

  const parsedCategories = orderedCategories?.slice(0, 6)?.map(mapCategory);

  const parsedAllCategories = filteredCategories?.map(mapCategory);

  // OPX-2721: Filtering category "Sustainability" (id 39 in TrustYou response)
  const parsedGoodToKnowList = tyResponse?.good_to_know_list
    ?.filter((goodToKnow: GoodToKnowResponse): boolean => goodToKnow.category_id !== '39')
    ?.map(mapGoodToKnow);

  const parsedRatingByTravelTypeList = tyResponse?.trip_type_meta_review_list?.map(mapRatingByType);

  const languageMetaReviewList = tyResponse?.language_meta_review_list?.map(
    (metaReview: LanguageMetaReviewListResponse): RatingByLanguage => {
      const goodToKnowList = metaReview.good_to_know_list
        ?.filter((goodToKnow: GoodToKnowResponse): boolean => goodToKnow.category_id !== '39') // OPX-2721: Filtering category "Sustainability" (id 39 in TrustYou response)
        ?.map(mapGoodToKnow);
      return {
        language: metaReview.filter.language,
        percentage: metaReview.reviews_percent,
        score: metaReview.summary.score,
        categories: metaReview.category_list
          ?.filter((category: CategoryResponse): boolean => category.category_id !== '39') // OPX-2721: Filtering category "Sustainability" (id 39 in TrustYou response)
          ?.map(mapCategory),
        goodToKnowList,
        ratingByTypeList: metaReview.trip_type_meta_review_list?.map(mapRatingByType) || [],
        summaryText: getSummaryText(goodToKnowList),
      };
    }
  );

  const badges =
    tyResponse?.badge_list?.map((badge: BadgeListResponse): RatingBadge => ({
      type: badge.badge_data.category_id ? `${badge.badge_type}-${badge.badge_data.category_id}` : badge.badge_type,
      text: badge.text,
      subtext: badge.subtext,
    })) || [];

  // eslint-disable-next-line consistent-return
  return {
    reviewCount: tyResponse?.reviews_count,
    score: tyResponse?.summary.score,
    scoreDescription: tyResponse?.summary.score_description,
    summaryText: tyResponse?.summary.text,
    categories: parsedCategories,
    allCategories: parsedAllCategories,
    goodToKnowList: parsedGoodToKnowList,
    ratingByTypeList: parsedRatingByTravelTypeList,
    ratingByLanguage: languageMetaReviewList,
    badges,
  };
};

export const useMetaReview = (trustYouHotelId: string | undefined, language: string, condition = true) => {
  const key =
    condition && trustYouHotelId
      ? `https://api.trustyou.com/hotels/${trustYouHotelId}/meta_review.json?lang=${(language ?? 'en').toLowerCase()}&key=${TRUSTYOU_API_KEY}`
      : undefined;

  const { data, error, isLoading } = useSWR(
    key,
    async (url) => tyFetcher(url).then((res) => tyMapper(res)),
    options(trustYouHotelId)
  );

  return {
    metaReview: data,
    isLoading,
    isError: error,
  };
};
